// Central content model for Consilium Pro Workforce Solutions

export const COMPANY = {
  name: "Consilium Pro Workforce Solutions",
  legalName: "Consilium Pro Workforce Solutions, LLC",
  short: "Consilium Pro",
  tagline: "Strategic people, stronger teams, lasting impact.",
  website: "www.consiliumpro.co",
  email: "info@consiliumpro.co",
  phone: "(678) 572-1813",
  phoneHref: "tel:+16785721813",
  hq: "Atlanta, Georgia",
  type: "Consulting & Fractional Services Firm",
  description:
    "Consilium Pro Workforce Solutions is a multidisciplinary consulting and fractional services firm providing organizations with strategic workforce, operational, administrative, technology, marketing, training, security, and business support solutions.",
  elevator:
    "We help organizations access the people, expertise, and operational support they need, without having to build everything internally.",
};

// Four delivery models, how Consilium Pro packages every engagement.
// Rendered on Home + Services as the top-level "How We Deliver" architecture.
export const DELIVERY_MODELS = [
  {
    slug: "consulting-advisory",
    id: "A",
    icon: "Compass",
    title: "Consulting & Advisory",
    tagline: "Assess. Advise. Design. Support.",
    short: "Assess the problem, advise the client, design the solution, and support implementation.",
    ideal: "Organizations facing a specific challenge, seeking strategic direction, or needing outside expertise for a discrete project.",
    includes: [
      "Assessments & audits",
      "Strategy & organizational improvement",
      "HR & workforce consulting",
      "Compliance & policy development",
      "Security consulting",
      "Technology strategy & assessment",
      "Project & business advisory",
    ],
  },
  {
    slug: "fractional-managed-services",
    id: "B",
    icon: "Handshake",
    title: "Fractional & Managed Services",
    tagline: "An ongoing extension of your team.",
    short: "Operate as an ongoing extension of your organization, without requiring a full-time internal hire.",
    ideal: "Growing organizations that need leadership or ongoing functional support but don't have the workload or budget for a full internal department.",
    includes: [
      "Fractional HR / People Operations",
      "Marketing & communications support",
      "Administrative & business support",
      "IT & technology support",
      "Training & client-success support",
      "Operations & project support",
    ],
  },
  {
    slug: "workforce-solutions",
    id: "C",
    icon: "Users",
    title: "Workforce Solutions",
    tagline: "Workforce, without the workload.",
    short: "We provide personnel who support your organization while remaining Consilium Pro employees, flexible, managed, and accountable.",
    ideal: "Organizations that need to supplement their workforce, fill gaps quickly, or integrate outside personnel without direct employment overhead.",
    includes: [
      "Temporary personnel",
      "Long-term assigned personnel",
      "Temp-to-hire arrangements",
      "Embedded personnel (Consilium Pro employees)",
      "Replacement & workforce-management support",
    ],
  },
  {
    slug: "career-professional-development",
    id: "D",
    icon: "GraduationCap",
    title: "Career & Professional Development",
    tagline: "For individuals investing in their next chapter.",
    short: "Direct services and education for individuals, our direct-to-consumer division that reinforces our workforce-development expertise.",
    ideal: "Professionals advancing their careers, preparing for job searches, or sharpening their executive presence.",
    audience: "Individuals · B2C",
    includes: [
      "Resume editing, updating & optimization",
      "Mock interviews",
      "Interview & job preparation",
      "Career readiness",
      "Professional communication & executive presence",
      "Live virtual & in-person workshops",
      "Self-paced digital courses (coming soon)",
    ],
  },
];

export const LOGOS = {
  full: "https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/mraw4ra6_CPWS%20Full%20Logo.PNG",
  wordmark: "https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/g5iiynt2_CPWS%20LinkedIn%20Banner.webp",
  mark: "https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/jrzcllwl_CP%20Logo%20Transparent.png",
};

export const SOCIALS = [
  { name: "LinkedIn",  icon: "linkedin",  href: "https://www.linkedin.com/company/consilium-pro-workforce-solutions/" },
  { name: "Instagram", icon: "instagram", href: "https://www.instagram.com/consilium.pro/" },
  { name: "Facebook",  icon: "facebook",  href: "https://www.facebook.com/profile.php?id=61593432992358" },
];

export const IMAGES = {
  heroAtlanta: "https://images.pexels.com/photos/33133727/pexels-photo-33133727.jpeg",
  executiveMeeting: "https://images.unsplash.com/photo-1758518727613-00192aed759b",
  diverseTeam: "https://images.pexels.com/photos/8068807/pexels-photo-8068807.jpeg",
  governmentBuilding: "https://images.unsplash.com/photo-1594581979864-36977b15d0dc",
};

// ---------- 13 productized Solutions (§4) ----------
// Each solution rides one or more of the four DELIVERY_MODELS.
// Cards render the 5-part structure: who / what / receive / length / next_step.
// Pricing intentionally omitted, will be introduced only after formal approval.
export const SOLUTIONS = [
  {
    slug: "workforce-readiness-diagnostic",
    icon: "Gauge",
    title: "Workforce Readiness Diagnostic",
    tagline: "A structured baseline of your people, process, and compliance posture.",
    who: "Small- and mid-sized organizations that suspect their people infrastructure has fallen behind their growth curve.",
    what: "We assess workforce planning, HR compliance, hiring pipelines, employee lifecycle systems, and organizational risk in one integrated review.",
    receive: "Findings memo · gap map · prioritized remediation roadmap · executive readout.",
    length: "2 3 weeks",
    next_step: "assessment",
    delivery_models: ["consulting-advisory"],
  },
  {
    slug: "hr-infrastructure-assessment",
    icon: "ClipboardCheck",
    title: "HR Infrastructure Assessment",
    tagline: "A deep review of the HR systems that carry the organization day-to-day.",
    who: "Executives who need a defensible read on policies, handbooks, compliance posture, and employee-relations exposure.",
    what: "We audit handbook coverage, wage & hour posture, classification, onboarding/offboarding, performance systems, and record-keeping.",
    receive: "Compliance heat map · policy gap list · handbook & workflow recommendations · executive readout.",
    length: "3 4 weeks",
    next_step: "assessment",
    delivery_models: ["consulting-advisory"],
  },
  {
    slug: "fractional-people-operations",
    icon: "UserCog",
    title: "Fractional People Operations",
    tagline: "Embedded senior HR leadership without the full-time hire.",
    who: "Organizations between 20 200 people that need senior HR judgment on retainer but not a full-time Head of People.",
    what: "Ongoing HR partnership: employee relations, hiring cadence, compliance oversight, manager coaching, and cross-functional advisory.",
    receive: "Designated Principal · monthly cadence · quarterly business reviews · on-call ER support.",
    length: "6 12 month engagements, renewable",
    next_step: "consultation",
    delivery_models: ["fractional-managed-services"],
  },
  {
    slug: "embedded-workforce-solutions",
    icon: "Users",
    title: "Embedded Workforce Solutions",
    tagline: "Consilium Pro professionals working alongside your team.",
    who: "Clients who need long-horizon capacity in HR, admin, operations, security, or technical roles without expanding their headcount.",
    what: "We recruit, employ, supervise, and deploy specialists who remain on Consilium Pro's payroll while supporting the client organization.",
    receive: "Assigned professional(s) · onboarded to client environment · Consilium Pro HR & payroll infrastructure · replacement guarantee per agreement.",
    length: "3+ month engagements",
    next_step: "consultation",
    delivery_models: ["workforce-solutions"],
  },
  {
    slug: "government-contractor-readiness",
    icon: "Landmark",
    title: "Government Contractor Readiness",
    tagline: "Get positioned to compete for federal, state, and local opportunities.",
    who: "Firms preparing to enter (or scale in) government contracting who need a defensible readiness baseline.",
    what: "We review SAM.gov, UEI/CAGE posture, NAICS/PSC alignment, socioeconomic classifications, capability statement, and past-performance documentation.",
    receive: "Readiness scorecard · registration checklist · positioning brief · one-page capability statement draft.",
    length: "3 5 weeks",
    next_step: "consultation",
    delivery_models: ["consulting-advisory"],
  },
  {
    slug: "organizational-audit",
    icon: "FileSearch",
    title: "Organizational Audit",
    tagline: "One view of people, process, systems, and structure.",
    who: "Boards, founders, and executive teams heading into a strategic inflection, funding, acquisition, restructuring, or new leadership.",
    what: "A cross-practice audit combining HR, operations, technology, communications, and compliance perspectives from multiple Principals.",
    receive: "Multi-practice findings memo · structural recommendations · risk register · executive briefing.",
    length: "4 6 weeks",
    next_step: "consultation",
    delivery_models: ["consulting-advisory", "strategic-partner"],
  },
  {
    slug: "leadership-development-training",
    icon: "GraduationCap",
    title: "Leadership Development & Training",
    tagline: "Practical training built by practitioners who have led teams.",
    who: "Organizations investing in new managers, front-line leadership, or executive-team development.",
    what: "Custom curriculum + live facilitation covering leadership foundations, coaching, communication, difficult conversations, and team performance.",
    receive: "Cohort plan · facilitator (Principal) · curriculum & workbooks · post-program measurement.",
    length: "4 12 week cohorts",
    next_step: "consultation",
    delivery_models: ["consulting-advisory", "fractional-managed-services"],
  },
  {
    slug: "technology-cybersecurity-readiness",
    icon: "ShieldCheck",
    title: "Technology Strategy & Assessment",
    tagline: "Independent, practical, aligned to how your business actually runs.",
    who: "Organizations preparing for audit, insurance underwriting, growth, or a regulated engagement who need a defensible tech baseline.",
    what: "Independent assessment of your technology stack, systems evaluation, workflow automation opportunities, AI readiness, and technology governance. Advisory only, no IT or cybersecurity delivery.",
    receive: "Findings memo · prioritized remediation plan · reference architecture · executive briefing.",
    length: "3 6 weeks",
    next_step: "assessment",
    delivery_models: ["consulting-advisory"],
  },
  {
    slug: "marketing-communications-support",
    icon: "Megaphone",
    title: "Marketing & Communications Support",
    tagline: "Sharpen how the organization is understood by every audience that matters.",
    who: "Executive teams whose external messaging has drifted from where the organization actually is.",
    what: "Brand review, messaging refresh, content strategy, stakeholder communications, and public affairs planning.",
    receive: "Messaging framework · communications calendar · brand & content guidelines · optional retainer roadmap.",
    length: "4 8 weeks (project) or ongoing (retainer)",
    next_step: "consultation",
    delivery_models: ["consulting-advisory", "fractional-managed-services"],
  },
  {
    slug: "operational-readiness-assessment",
    icon: "Wrench",
    title: "Operational Readiness Assessment",
    tagline: "Logistics, infrastructure, and workflow, reviewed as one system.",
    who: "Field-driven organizations, security firms, logistics operators, and government contractors preparing for scale or scrutiny.",
    what: "Assessment of operational workflows, fleet/asset posture, facility readiness, SOP coverage, and mission continuity.",
    receive: "Readiness scorecard · SOP gaps · asset & vendor recommendations · continuity brief.",
    length: "3 5 weeks",
    next_step: "assessment",
    delivery_models: ["consulting-advisory"],
  },
  {
    slug: "recruiting-talent-acquisition",
    icon: "UserSearch",
    title: "Recruiting & Talent Acquisition Support",
    tagline: "Fill priority roles without breaking your operating rhythm.",
    who: "Growing organizations that need senior-level recruiting horsepower for a discrete hiring window.",
    what: "Sourcing, screening, interview design, and offer-strategy support for named roles, from front-line to executive.",
    receive: "Slate of qualified finalists · calibrated interview loop · offer-strategy support · post-hire onboarding notes.",
    length: "45 90 days per role",
    next_step: "consultation",
    delivery_models: ["fractional-managed-services", "workforce-solutions"],
  },
  {
    slug: "temporary-long-term-workforce-support",
    icon: "CalendarClock",
    title: "Temporary & Long-Term Workforce Support",
    tagline: "Skilled professionals on Consilium Pro's payroll, deployed to your team.",
    who: "Clients who need workforce coverage for surges, transitions, project ramps, or long-horizon staffing.",
    what: "Temporary, temp-to-hire, long-term, embedded, or project-based professionals across HR, admin, marketing, IT, security, and operations.",
    receive: "Assigned professional(s) · managed by Consilium Pro HR · engagement terms per client agreement.",
    length: "Variable, temp, project, embedded, or long-term",
    next_step: "consultation",
    delivery_models: ["workforce-solutions"],
  },
  {
    slug: "strategic-project-support",
    icon: "Rocket",
    title: "Strategic Project Support",
    tagline: "Senior hands on the moves that matter most.",
    who: "Executive teams executing a defined strategic initiative, launch, integration, transition, or high-stakes program.",
    what: "Project leadership and cross-practice specialists deployed to plan, execute, and land a named initiative.",
    receive: "Project plan · assigned Principal(s) & specialists · milestone reviews · handover documentation.",
    length: "6 20 week engagements",
    next_step: "consultation",
    delivery_models: ["consulting-advisory", "strategic-partner"],
  },
];

// ---------- FAQ (§20) ----------
export const FAQ = [
  { q: "What is Consilium Pro Workforce Solutions?", a: "Consilium Pro Workforce Solutions is a Principal-led consulting and workforce firm. We advise organizations on people, operations, compliance, technology, growth, and government readiness, and we can also employ and deploy professionals into client environments through our workforce model." },
  { q: "What does fractional support mean?", a: "Fractional means a senior practitioner works with your organization on an ongoing, part-time basis, typically a set cadence per week or per month. It gives you executive-level judgment without the cost or commitment of a full-time hire." },
  { q: "Is Consilium Pro a staffing agency?", a: "No. We are a consulting and workforce solutions firm. Where we deploy professionals into client environments, they remain Consilium Pro employees supported by our HR infrastructure, and they work alongside Principal-led advisory and delivery." },
  { q: "Who employs an embedded Consilium Pro worker?", a: "The professional remains an employee of Consilium Pro Workforce Solutions. Consilium Pro manages payroll, HR administration, and the employment relationship. Specific supervision, replacement, and conversion terms are set in the client agreement." },
  { q: "Can a client permanently hire a Consilium Pro employee?", a: "In many engagements, yes, through a temp-to-hire or conversion arrangement. Conversion terms, notice, and any associated fees are defined in the client agreement." },
  { q: "Do you support organizations outside Georgia?", a: "Yes. We are Atlanta-based and provide nationwide support for consulting engagements. Workforce deployments outside Georgia are supported on a case-by-case basis based on the role and jurisdiction." },
  { q: "Do you work with small businesses?", a: "Yes. Small and growing businesses are one of our priority markets. We shape engagements to match a small business's budget, risk profile, and stage." },
  { q: "Do you support government contractors?", a: "Yes. We support government contractors on readiness, capability positioning, workforce, compliance, and teaming. We are actively building out our own government registrations and will publish those once officially confirmed." },
  { q: "Do you work directly with government agencies?", a: "We are positioning to support government agency work as our registrations are finalized. In the interim, we support agencies indirectly through prime contractor teaming arrangements." },
  { q: "Can we purchase only one service?", a: "Yes. Every productized solution is available as a standalone engagement. Many clients start with a single diagnostic or assessment and expand from there." },
  { q: "Can multiple Principals support one engagement?", a: "Yes. That is one of the strengths of a Principal-led, multidisciplinary firm. When a challenge spans practices, we assemble the right combination of Principals and specialists around it." },
  { q: "How long does an engagement last?", a: "It depends on the solution. Assessments typically run 2 6 weeks. Fractional and embedded engagements run in multi-month terms. Strategic project support and workforce deployments are scoped to the engagement." },
  { q: "What happens after the assessment?", a: "You receive a written findings memo, a prioritized recommendation set, and a debrief with the responsible Principal. From there, clients typically choose to remediate independently, engage Consilium Pro to execute, or convert into an ongoing partnership." },
  { q: "Do you offer project-based engagements?", a: "Yes. Project-based engagements are one of our four delivery models. They are ideal when the scope, timeline, and outcome are well-defined at the outset." },
  { q: "Do you provide long-term embedded personnel?", a: "Yes. Long-term embedded professionals are one of our workforce arrangements, subject to the terms of the client agreement." },
  { q: "How do we request a proposal?", a: "Schedule a consultation and share your context. A responsible Principal will meet, scope, and issue a Strategic Consulting Proposal with the recommended engagement structure." },
];

// ---------- Case Studies & Representative Solutions (§17) ----------
// Three strict categories, never combined:
//   client_engagement  , real Consilium Pro delivery (only added once documented)
//   representative     , anonymized/hypothetical example of how we would engage
//   leadership_experience  prior individual practitioner experience (attributed to a Principal, not Consilium Pro)
export const CASE_TYPES = {
  client_engagement:    { label: "Client Engagement",       accent: "text-emerald-700 border-emerald-200 bg-emerald-50" },
  representative:       { label: "Representative Solution", accent: "text-navy border-gold bg-gold/10" },
  leadership_experience:{ label: "Leadership Experience",   accent: "text-navy/70 border-navy/20 bg-lightgray" },
};

export const CASE_STUDIES = [
  {
    id: "rep-fractional-hr-scaleup",
    type: "representative",
    title: "Fractional HR for a 40-person scale-up",
    challenge: "A 40-person services firm is scaling past the point where founder-led HR is sustainable. Employee-relations complexity is rising and policies are ad-hoc.",
    environment: "Professional services · Southeast US · privately held · 40 headcount.",
    approach: "Embed a Consilium Pro Principal as fractional Head of People, weekly cadence, monthly executive review, on-call ER support, and a 90-day roadmap addressing handbook, classification, hiring, and performance.",
    deliverables: "Handbook refresh · classification review · manager-coaching cadence · ATS process · quarterly people-operations reporting.",
    outcome: "Founder recovers ~8 hours per week previously spent on HR triage. Policy and classification exposure documented and remediated. Executive team gains a predictable people-operations rhythm.",
    services: ["fractional-people-operations", "hr-infrastructure-assessment"],
    principal: "Kentavious D. Patman",
  },
  {
    id: "rep-gov-readiness",
    type: "representative",
    title: "Federal contracting readiness for a commercial janitorial company",
    challenge: "A regional commercial janitorial company is targeting its first federal facilities contracts but lacks a defensible readiness posture.",
    environment: "Commercial janitorial & facilities services · privately held · commercially focused · 60 headcount.",
    approach: "Deploy the Government Contractor Readiness solution, review SAM/UEI/NAICS posture, socioeconomic positioning, capability statement, and past-performance documentation. Assemble a lightweight capture readiness plan.",
    deliverables: "Readiness scorecard · registration & positioning checklist · capability statement draft · target opportunity list.",
    outcome: "Company exits the engagement with a defensible readiness baseline, a decision-ready target list, and the documentation needed to pursue prime and subcontract facilities opportunities.",
    services: ["government-contractor-readiness"],
    principal: "Kentavious D. Patman",
  },
  {
    id: "rep-embedded-ops",
    type: "representative",
    title: "Embedded operations & concierge support for a growing services firm",
    challenge: "An expanding services firm needs consistent operational and concierge coverage but is not ready to hire full-time roles.",
    environment: "Growing multi-site services organization · Southeast US.",
    approach: "Deploy embedded Consilium Pro professionals across reception/concierge, operations coordination, and administrative support, supervised locally, employed by Consilium Pro.",
    deliverables: "Assigned team · onboarding to client environment · SOPs · defined replacement & conversion terms per agreement.",
    outcome: "Client gains consistent operational coverage without the HR infrastructure cost, and preserves the ability to convert high-performing professionals into full-time hires per the agreement.",
    services: ["embedded-workforce-solutions", "temporary-long-term-workforce-support"],
    principal: "Jeremy Cortez",
  },
  {
    id: "le-geospatial-ncoic",
    type: "leadership_experience",
    title: "GEOINT NCOIC leadership · Georgia Army National Guard",
    challenge: "Lead a geospatial intelligence team responsible for structured analysis and mission-support products.",
    environment: "Military intelligence · Georgia Army National Guard.",
    approach: "Supervised analyst workflow, quality control, and readiness cadence across multiple exercises and mission cycles.",
    deliverables: "Structured intelligence products · trained analyst team · sustained readiness posture.",
    outcome: "Prior individual leadership experience, not Consilium Pro contract performance.",
    services: [],
    principal: "Brashad Rouland",
  },
  {
    id: "le-training-tech-orgs",
    type: "leadership_experience",
    title: "Enterprise training delivery at major technology organizations",
    challenge: "Deliver technical enablement and adoption training at scale to enterprise audiences.",
    environment: "Global technology organizations · enterprise adoption programs.",
    approach: "Facilitated live training, executive briefings, and product enablement sessions across geographies.",
    deliverables: "Facilitated sessions · adoption curriculum · post-program feedback loops.",
    outcome: "Prior individual practitioner experience, not Consilium Pro contract performance.",
    services: [],
    principal: "Kentavious D. Patman",
  },
];

// ---------- Workforce Solutions page (§5) ----------
export const WORKFORCE_MODEL_TYPES = [
  { key: "temporary",     title: "Temporary",     desc: "Short-horizon coverage for surges, gaps, or defined windows." },
  { key: "long_term",     title: "Long-Term",     desc: "Sustained, multi-quarter coverage without expanding client headcount." },
  { key: "temp_to_hire",  title: "Temp-to-Hire",  desc: "Deploy today; convert to a full-time hire per the client agreement." },
  { key: "embedded",      title: "Embedded",      desc: "Professionals working inside the client environment as part of the team." },
  { key: "project_based", title: "Project-Based", desc: "Scoped to a discrete initiative with defined start and end." },
];

export const WORKFORCE_ROLE_CATEGORIES = [
  { label: "Human Resources",             icon: "Users" },
  { label: "Marketing & Communications",  icon: "Megaphone" },
  { label: "Accounting",                  icon: "Coins" },
  { label: "Administrative Support",      icon: "ClipboardList" },
  { label: "Office Management",           icon: "Building2" },
  { label: "IT",                          icon: "Cpu" },
  { label: "Cybersecurity",               icon: "ShieldCheck" },
  { label: "Reception",                   icon: "Bell" },
  { label: "Concierge",                   icon: "Star" },
  { label: "Project Support",             icon: "KanbanSquare" },
];



export const SERVICES = [
  {
    slug: "executive-strategy-human-capital",
    icon: "Users",
    title: "People, HR & Workforce Solutions",
    short: "HR infrastructure, compliance, recruiting, employee lifecycle, workforce planning, performance, and fractional people support.",
    hero: "Build the people infrastructure needed to hire, manage, develop, and retain a stronger workforce, from HR foundations to embedded fractional HR partnership.",
    delivery_models: ["consulting-advisory", "fractional-managed-services", "workforce-solutions"],
    capabilities: [
      "HR Assessment & Audit", "Policies & Handbooks", "Recruiting & Talent",
      "Onboarding & Offboarding", "Performance & Employee Relations",
      "Compensation & Benefits Advisory", "Fractional People Operations",
      "Workforce Strategy", "Terminations & Separations",
      "Change Management", "Compliance",
    ],
  },
  {
    slug: "technology-cybersecurity-ai",
    icon: "Cpu",
    title: "Technology & Digital Solutions",
    short: "Technology strategy and assessment, website creation and management, digital workflows, workflow automation, CRM, vendor evaluation, and technology roadmaps.",
    hero: "Align technology, systems, data, and digital workflows to the way your organization actually operates.",
    delivery_models: ["consulting-advisory", "fractional-managed-services"],
    capabilities: [
      "Technology Assessment", "IT Strategy & Roadmap", "Technology Governance & AI Readiness",
      "Systems & Software Selection", "CRM & Customer Systems",
      "Automation & Workflows", "Workspace Optimization",
      "IT Policies & Digital Governance", "Digital Transformation",
      "Website Creation & Management", "AI Readiness",
    ],
  },
  {
    slug: "marketing-brand-communications",
    icon: "Megaphone",
    title: "Marketing, Brand & Public Affairs",
    short: "Brand strategy, content, digital marketing, public relations, communications, reputation, campaigns, and stakeholder engagement.",
    hero: "Strengthen how your organization is seen, heard, understood, and trusted, across brand, digital, and public affairs.",
    delivery_models: ["consulting-advisory", "fractional-managed-services"],
    capabilities: [
      "Brand Strategy", "Marketing Strategy", "Content Creation",
      "Social Media", "Digital Marketing", "Public Relations",
      "Public Affairs & Stakeholders", "Reputation & Crisis Communications",
      "Event & Campaign Promotion", "Internal Communications",
    ],
  },
  {
    slug: "business-development-intelligence-geospatial",
    icon: "Target",
    title: "Growth, Sales & Partnerships",
    short: "Sales strategy, pipeline development, business development, account growth, community partnerships, outreach, and market expansion.",
    hero: "Build a repeatable path from opportunity to relationship to revenue, sales strategy, pipeline discipline, and community partnerships that drive lasting growth.",
    delivery_models: ["consulting-advisory", "fractional-managed-services"],
    capabilities: [
      "Sales Strategy", "Sales Pipeline", "Business Development",
      "Sales Enablement", "Community Partnerships", "Account Growth",
      "Market Expansion", "Revenue Operations",
      "Capture Strategy", "Competitive Intelligence", "Geospatial Intelligence",
    ],
  },
  {
    slug: "learning-leadership-client-success",
    icon: "GraduationCap",
    title: "Training, Leadership & Client Success",
    short: "Workforce development, leadership training, safety courses, curriculum consulting, career readiness, onboarding, SOP-based training, and learning programs.",
    hero: "Turn expectations into repeatable performance through training, leadership development, and client-experience systems.",
    delivery_models: ["consulting-advisory", "fractional-managed-services", "career-professional-development"],
    capabilities: [
      "Training Needs Analysis", "Custom Training", "Leadership Development",
      "Onboarding & Role Readiness", "LMS & Learning Operations",
      "Client Success", "Customer Experience",
      "Facilitation & Workshops", "CPR, First Aid & AED Training",
      "Curriculum Consulting", "Career Readiness & Interview Prep",
      "Private Facilitation & Conflict Coaching",
    ],
  },
  {
    slug: "infrastructure-fleet-operational-readiness",
    icon: "Truck",
    title: "Logistics, Infrastructure & Operations",
    short: "Operational workflows, logistics, dedicated workforce staffing, concierge and administrative support, procurement, facilities, fleet advisory, and project support.",
    hero: "Improve the systems, resources, vendors, facilities, and workflows that keep the organization moving, from SOP design to embedded operations partnership.",
    delivery_models: ["consulting-advisory", "fractional-managed-services", "workforce-solutions"],
    capabilities: [
      "Operations Assessment", "Process & SOP Design", "Logistics Planning",
      "Fleet Advisory", "Facilities & Infrastructure",
      "Vendor & Procurement Support", "Project & Program Support",
      "Business Continuity Readiness", "Concierge & Administrative Staffing (GA)",
      "Temporary & Dedicated Office Staffing", "Temp-to-Hire & Direct-Hire Conversion",
    ],
  },
  {
    slug: "government-defense-solutions",
    icon: "Landmark",
    title: "Security, Government & High-Compliance Solutions",
    short: "Advisory only, security program review, risk & vulnerability assessments, compliance consulting, government contractor readiness, and mission-focused consulting. Consilium Pro does not provide or employ security guarding personnel.",
    hero: "Advisory support for organizations operating where readiness, documentation, discipline, and compliance matter, from security consulting to government contractor readiness.",
    delivery_models: ["consulting-advisory", "fractional-managed-services", "workforce-solutions"],
    capabilities: [
      "Security Consulting", "Security Risk & Site Vulnerability Assessments",
      "Physical Security Assessments", "Security Program Reviews",
      "Security Compliance Consulting", "Security Policy & Procedure Development",
      "Security Audits & Corrective Action", "Risk Management Advisory",
      "Emergency Preparedness Consulting", "Security Procurement / RFP Advisory",
      "Protective Security Strategy", "Security Training & Program Development",
      "Government Contractor Readiness", "Program & Administrative Support",
      "Compliance-Driven Workforce Support", "Proposal & Opportunity Support",
      "Mission-Focused Consulting", "SAM.gov Registration Assistance",
    ],
  },
];

// Six homepage feature cards mapped to service pages
export const FEATURE_CARDS = [
  { label: "People, HR & Workforce", icon: "Users", slug: "executive-strategy-human-capital" },
  { label: "Technology & Digital", icon: "Cpu", slug: "technology-cybersecurity-ai" },
  { label: "Marketing, Brand & Public Affairs", icon: "Megaphone", slug: "marketing-brand-communications" },
  { label: "Growth, Sales & Partnerships", icon: "Target", slug: "business-development-intelligence-geospatial" },
  { label: "Training, Leadership & Client Success", icon: "GraduationCap", slug: "learning-leadership-client-success" },
  { label: "Logistics, Infrastructure & Operations", icon: "Truck", slug: "infrastructure-fleet-operational-readiness" },
  { label: "Security, Government & Compliance", icon: "Landmark", slug: "government-defense-solutions" },
];

// Engagement models, how any package can be delivered
export const ENGAGEMENT_LEVELS = [
  { name: "Foundation",         icon: "Search",       desc: "When the client needs clarity. Discovery, assessment, gap analysis, recommendations, priorities, and a practical roadmap." },
  { name: "Implementation",     icon: "Wrench",       desc: "When the client needs the solution built. Policies, workflows, campaigns, systems, training, content, documentation, and implementation support." },
  { name: "Managed Support",    icon: "Repeat",       desc: "When the client needs ongoing execution. Recurring deliverables, meetings, reporting, optimization, and continuous support in defined areas." },
  { name: "Strategic Partner",  icon: "Handshake",    desc: "When the client needs sustained leadership-level support. Embedded or fractional consulting, cross-functional coordination, planning, operating reviews, and change execution." },
];

export const INDUSTRIES = [
  { name: "Commercial", icon: "Building2", desc: "Growth-stage and established enterprises modernizing operations and workforce." },
  { name: "Government", icon: "Landmark", desc: "Federal, state, and local agencies advancing mission delivery." },
  { name: "Government Contractors", icon: "FileSignature", desc: "Contractors pursuing capture, compliance, and proposal excellence." },
  { name: "Defense", icon: "Shield", desc: "Defense organizations requiring secure, mission-ready advisory." },
  { name: "Healthcare", icon: "HeartPulse", desc: "Providers balancing compliance, workforce, and patient outcomes." },
  { name: "Security", icon: "Lock", desc: "Security consulting, risk assessments, program design, and compliance advisory." },
  { name: "Manufacturing", icon: "Factory", desc: "Operational readiness, workforce, and continuity for production." },
  { name: "Technology", icon: "Cpu", desc: "Scaling tech firms aligning talent, product, and go-to-market." },
  { name: "Transportation", icon: "Bus", desc: "Fleet, logistics, and infrastructure optimization." },
  { name: "Logistics", icon: "PackageCheck", desc: "Supply chain resilience and operational excellence." },
  { name: "Construction", icon: "HardHat", desc: "Infrastructure planning and workforce mobilization." },
  { name: "Faith-Based Organizations", icon: "Church", desc: "Mission-driven organizations strengthening operations and people." },
  { name: "Nonprofits", icon: "HandHeart", desc: "Purpose-led teams improving capacity and impact." },
  { name: "Education", icon: "GraduationCap", desc: "Institutions building leadership and operational capability." },
  { name: "Utilities", icon: "Zap", desc: "Continuity, readiness, and workforce for critical services." },
];

// 5 priority-market landing pages (§18), dedicated /industries/{slug}
// Every entry ships the shared 6-block structure and uses "We support" language.
export const INDUSTRY_LANDINGS = [
  {
    slug: "security-companies",
    name: "Security Companies",
    icon: "Lock",
    hero: "Advisory support, HR infrastructure, operational readiness, and government positioning, for firms operating in the security services sector.",
    challenges: [
      "Turnover and staffing volatility affecting client contracts",
      "Compliance posture (licensing, DHS/state) that shifts by jurisdiction",
      "Uneven training and SOP coverage across sites and shifts",
      "Difficulty pursuing government contracts without registration & positioning support",
    ],
    helps: "We support security operators with HR infrastructure, government readiness, and operational discipline, advising on programs, policies, training, and workforce systems. Consilium Pro is a consultancy: we advise on security programs and provide administrative and back-office workforce coverage. We do not provide, manage, or employ security guarding personnel.",
    solutions: ["hr-infrastructure-assessment", "fractional-people-operations", "embedded-workforce-solutions", "government-contractor-readiness", "operational-readiness-assessment"],
    principals: ["Brashad Rouland", "Kentavious D. Patman"],
    example: "Representative Solution, a regional commercial janitorial company pursuing federal opportunities. We stand up the readiness scorecard, capability statement, and NAICS/PSC positioning; parallel to the readiness track, we embed a fractional HR partner to tighten policy, training, and site SOPs.",
  },
  {
    slug: "small-growing-businesses",
    name: "Small & Growing Businesses",
    icon: "TrendingUp",
    hero: "Right-sized HR, operations, and technology support for teams outgrowing DIY.",
    challenges: [
      "Founder-led HR that has become a full-time job",
      "Policies, handbooks, and classification that never got written down",
      "Hiring bursts that outpace the infrastructure supporting them",
      "Technology and compliance decisions with no in-house owner",
    ],
    helps: "We support small and growing businesses with fractional people-operations, HR audits, and workforce coverage, scaled to your headcount, budget, and risk profile.",
    solutions: ["workforce-readiness-diagnostic", "hr-infrastructure-assessment", "fractional-people-operations", "recruiting-talent-acquisition", "technology-cybersecurity-readiness"],
    principals: ["Kentavious D. Patman", "Brashad Rouland"],
    example: "Representative Solution, a 40-person professional services firm scaling past founder-led HR. We embed a fractional Head of People, refresh the handbook, install a monthly cadence, and provide on-call employee-relations support.",
  },
  {
    slug: "government-contractors",
    name: "Government Contractors",
    icon: "FileSignature",
    hero: "Capability, readiness, and teaming support for firms competing in the federal space.",
    challenges: [
      "SAM/UEI/CAGE posture that needs a defensible baseline",
      "Capability statement, NAICS/PSC, and past-performance positioning gaps",
      "Uneven proposal, capture, and teaming discipline",
      "Workforce and compliance requirements the commercial side didn't demand",
    ],
    helps: "We support government contractors with Contractor Readiness reviews, capability positioning, and workforce coverage. Where teaming is the play, we can sub or prime under an executed agreement.",
    solutions: ["government-contractor-readiness", "hr-infrastructure-assessment", "operational-readiness-assessment", "recruiting-talent-acquisition", "strategic-project-support"],
    principals: ["Kentavious D. Patman", "Darien Ford", "Brashad Rouland"],
    example: "Representative Solution, a services firm pursuing its first prime opportunity. We deliver the readiness scorecard, capability statement, and target-opportunity list; we coordinate a teaming conversation to backstop past-performance requirements.",
  },
  {
    slug: "government-agencies",
    name: "Government Agencies",
    icon: "Landmark",
    hero: "Practitioner-led advisory for public-sector teams under compliance and mission pressure.",
    challenges: [
      "Workforce planning that has to survive turnover and civil-service cadences",
      "Training, curriculum, and leadership development at scale",
      "Technology modernization inside compliance perimeters",
      "Continuity and readiness across mission cycles",
    ],
    helps: "We support government agency work today through prime contractor teaming arrangements and Principal-led advisory. Direct contracting is being positioned as our registrations are finalized.",
    solutions: ["leadership-development-training", "technology-cybersecurity-readiness", "operational-readiness-assessment", "strategic-project-support"],
    principals: ["Kentavious D. Patman", "Darien Ford", "Jeremy Cortez", "Brashad Rouland"],
    example: "Representative Solution, a mission office facing training-quality drift across sites. We design a curriculum, deploy a Principal facilitator, and stand up a post-program measurement loop tied to observable outcomes.",
  },
  {
    slug: "faith-based-nonprofit",
    name: "Faith-Based & Nonprofit Organizations",
    icon: "Church",
    hero: "Right-sized people, operations, and communications support for mission-driven teams.",
    challenges: [
      "Growing programs outpacing back-office infrastructure",
      "Volunteer / paid workforce blends that need clean policies",
      "Communications and stakeholder engagement stretched by capacity",
      "Compliance, grants, and reporting demands rising over time",
    ],
    helps: "We support faith-based and nonprofit organizations with HR foundations, fractional operations support, communications strategy, and training, scaled to mission and budget.",
    solutions: ["hr-infrastructure-assessment", "fractional-people-operations", "marketing-communications-support", "leadership-development-training", "operational-readiness-assessment"],
    principals: ["Kentavious D. Patman", "Hadiya Rucker", "Brashad Rouland"],
    example: "Representative Solution, a growing faith-based organization strengthening its people and communications posture. We refresh the handbook, embed light people-operations coverage, and stand up an annual communications calendar tied to key programs.",
  },
];

// ---------- For Professionals (§21), 8 service tiers ----------
// These are the B2C offerings surfaced on `/career-services` (rebranded as
// "For Professionals · Career Services & Professional Development").
export const B2C_SERVICE_TIERS = [
  { slug: "resume-review",           title: "Resume Review",              tagline: "A senior second set of eyes.",              summary: "Line-level review of your resume with clear, prioritized recommendations you can execute yourself.",                  icon: "FileSearch" },
  { slug: "resume-writing",          title: "Resume Writing & Optimization", tagline: "ATS-ready. Recruiter-tested.",           summary: "Full rewrite with modern formatting, quantified impact, and keyword alignment to your target roles.",                    icon: "FileText" },
  { slug: "linkedin-optimization",   title: "LinkedIn Profile Optimization",tagline: "Get found for the right roles.",           summary: "Headline, About section, Featured, and Experience overhauled so your profile searches, and reads, like your target role.", icon: "Linkedin" },
  { slug: "mock-interviews",         title: "Mock Interview Preparation",   tagline: "Practice the room before you're in it.",   summary: "One-on-one mock interview tailored to your role and industry. Recorded, reviewed, and returned with a coaching plan.",       icon: "MessageSquare" },
  { slug: "interview-coaching",      title: "Interview Coaching",           tagline: "From application to offer.",               summary: "End-to-end coaching: STAR responses, company research, and offer strategy, sequenced for your live search.",              icon: "Target" },
  { slug: "career-strategy",         title: "Career Strategy Sessions",     tagline: "Where you're going. How to get there.",    summary: "Structured session(s) with a senior practitioner to design your next chapter, from targeting to sequencing.",              icon: "Compass" },
  { slug: "executive-presence",      title: "Executive Presence & Professional Branding", tagline: "Show up like a leader.",     summary: "Presence, voice, executive communication, and personal-brand development for professionals stepping into leadership.",       icon: "Crown" },
  { slug: "career-readiness-coaching",title: "Career Readiness Coaching",   tagline: "Ready for what's next.",                  summary: "For candidates entering the workforce or returning after a break, personal brand, networking, and 30-day launch plan.",     icon: "Sparkles" },
];



export const WHY = [
  { icon: "Award", title: "Executive-Level Consulting", desc: "Senior practitioners who have led the work they now advise on." },
  { icon: "Compass", title: "Mission-Focused", desc: "Every engagement is anchored to your mission and measurable outcomes." },
  { icon: "Users", title: "Experienced Leadership", desc: "Deep cross-sector expertise across commercial, nonprofit, and government." },
  { icon: "Settings2", title: "Customized Solutions", desc: "No templates. Solutions engineered for your context and constraints." },
  { icon: "Landmark", title: "Government-Ready", desc: "Compliance-driven delivery built for public sector standards." },
  { icon: "Cpu", title: "Technology-Enabled", desc: "AI and data woven into strategy for durable advantage." },
  { icon: "Handshake", title: "Long-Term Partnerships", desc: "We measure success by the outcomes that outlast the engagement." },
];

export const VALUES = [
  { label: "People", desc: "We put people at the center of every strategy and system we design." },
  { label: "Purpose", desc: "Mission clarity drives our recommendations and your results." },
  { label: "Performance", desc: "Practical, measurable outcomes, not theory or slideware." },
  { label: "Integrity", desc: "Compliance-driven, transparent, and accountable in every engagement." },
  { label: "Partnership", desc: "We build durable relationships that compound in value over time." },
];

export const TIMELINE = [
  { year: "Founded", title: "A firm built on practitioners", desc: "Consilium Pro was formed by operators and advisors who had led transformation from the inside." },
  { year: "Today", title: "Cross-sector advisory", desc: "Serving commercial, nonprofit, and government clients across the United States from our Atlanta headquarters." },
  { year: "Next", title: "AI-powered services", desc: "Expanding into AI-enabled advisory, government contracting, and a future client portal." },
];

export const RESOURCES = [
  {
    slug: "workforce-readiness-playbook",
    type: "Playbook",
    title: "The Workforce Readiness Playbook",
    desc: "A practitioner framework for aligning people, operations, and compliance systems for organizational growth.",
    tag: "Workforce",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80",
    author: "Kentavious Patman",
    author_role: "Founder & Managing Principal",
    read_time: "8 min read",
    date: "2026-08-22",
    cta: { label: "Take the Business Assessment", to: "/business-assessment", eyebrow: "Diagnose your readiness" },
    body: [
      { kind: "p", text: "Workforce readiness is not a single initiative. It is the condition of an organization when its people, processes, systems, and compliance posture are aligned well enough to absorb growth, respond to disruption, and deliver on commitments without breaking. Most organizations we work with are strong in two or three of those areas, and quietly fragile in the rest." },
      { kind: "p", text: "This playbook lays out the practitioner framework we use to establish that baseline. It is written for founders, operations leaders, and executives who suspect the operating system beneath the strategy is not carrying the weight of the plan." },
      { kind: "h2", text: "The four pillars of workforce readiness" },
      { kind: "list", items: [
        "People, roles are defined, accountabilities are clear, and the leadership bench is honest about capacity.",
        "Process, the work that keeps the organization running is documented well enough to survive the loss of any one person.",
        "Systems, the technology stack supports the work rather than dictating it, and access, security, and data are governed.",
        "Compliance, obligations to clients, regulators, and the workforce itself are known, tracked, and demonstrably met.",
      ] },
      { kind: "h2", text: "Where fragility usually lives" },
      { kind: "p", text: "In practice, we see three recurring failure patterns. First, the organization has grown faster than its documentation, critical knowledge lives in one or two people. Second, the technology has been layered on rather than designed, every new tool solves a symptom and adds a seam. Third, compliance is treated as an event rather than a posture, the annual audit gets attention, the day-to-day discipline does not." },
      { kind: "h2", text: "How to sequence the work" },
      { kind: "p", text: "The sequencing matters as much as the interventions themselves. We start with a diagnostic baseline across the four pillars, then triage: what is actively bleeding, what is a scaling constraint, and what is durable. That triage, not a generic checklist, is what determines whether the next 90 days should be spent on documentation, structure, systems, or compliance controls." },
      { kind: "h2", text: "What comes after the baseline" },
      { kind: "p", text: "A workforce readiness baseline is a starting document, not a finished one. Its value is in what it triggers: a prioritized backlog, a decision on which capabilities to build internally versus source through fractional or embedded support, and a shared vocabulary for leadership to talk about the state of the operation with the same precision they use for revenue." },
    ],
  },
  {
    slug: "fractional-hr-when",
    type: "Article",
    title: "When Does a Small Business Need Fractional HR?",
    desc: "Signals that your organization has outgrown DIY HR and how a fractional model fills the gap.",
    tag: "Fractional Support",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1600&q=80",
    author: "Kentavious Patman",
    author_role: "Founder & Managing Principal",
    read_time: "6 min read",
    date: "2026-08-22",
    cta: { label: "Take the Business Assessment", to: "/business-assessment", eyebrow: "See what your HR posture actually looks like" },
    body: [
      { kind: "p", text: "There is a specific moment in the life of a small business when DIY HR stops being resourceful and starts being risky. It usually is not marked by a lawsuit or a bad hire, it is marked by the accumulation of small decisions that no one has time to make well. Offer letters go out inconsistently. Handbook updates lag the laws. Onboarding is different for every new hire. Documentation exists in someone's inbox." },
      { kind: "p", text: "That is the point at which fractional HR earns its keep." },
      { kind: "h2", text: "Five signals the DIY approach has run out" },
      { kind: "list", items: [
        "Leaders are making HR decisions in the margins of their real work, and knowing they are getting them 80% right at best.",
        "The organization has crossed a legal or regulatory threshold (ACA, EEO-1, FMLA, state-specific mandates) and the tracking is manual.",
        "A single person holds the institutional memory for hiring, discipline, benefits, and terminations, and their departure would be catastrophic.",
        "The team is above 15 20 employees and interpersonal issues have started to require formal process, not just conversation.",
        "The organization is preparing to bid on work, close a raise, or acquire another entity, and the diligence questions are exposing gaps.",
      ] },
      { kind: "h2", text: "What fractional HR actually does" },
      { kind: "p", text: "Fractional HR is not a discount version of a full-time hire. It is a different operating model. A fractional HR leader arrives with a mature toolkit, policies, systems, cadences, escalation paths, and installs it into your environment at the level of maturity you actually need today, with a clear plan for what you graduate into next." },
      { kind: "h2", text: "What it does not do" },
      { kind: "p", text: "A fractional model does not absolve leadership of ownership. It builds the operating layer and holds the line on discipline, but the culture, the standards, and the difficult conversations still belong to the executive team. Fractional works when leaders want a partner, not a heat shield." },
      { kind: "h2", text: "The right time is usually earlier than founders think" },
      { kind: "p", text: "Every founder we work with who waited says the same thing: they should have engaged sooner. The cost of unwinding six or twelve months of unmanaged HR decisions is meaningfully higher than the cost of preventing them. If two or more of the signals above are present, it is worth a conversation." },
    ],
  },
  {
    slug: "outgrown-operating-model",
    type: "Article",
    title: "7 Signs Your Organization Has Outgrown Its Current Operating Model",
    desc: "Practical indicators that it's time to restructure people, process, and technology in sync.",
    tag: "Operations",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80",
    author: "Kentavious D. Patman",
    author_role: "Founder & Managing Principal",
    read_time: "7 min read",
    date: "2026-08-22",
    cta: { label: "Schedule a Consultation", to: "/contact", eyebrow: "Pressure-test your operating model" },
    body: [
      { kind: "p", text: "An operating model is the way an organization actually gets work done, the roles, the handoffs, the tooling, the meeting rhythm, the decision rights. Organizations outgrow theirs quietly. The strategy still sounds right, the team is still capable, but the work feels harder than it should, and no one can say exactly why." },
      { kind: "p", text: "These are the seven signs we most often see." },
      { kind: "h2", text: "1. Every important decision routes to the same three people" },
      { kind: "p", text: "When the org chart is wide but the decision graph is a bottleneck, the model is undersized. Growth has outrun the delegation structure." },
      { kind: "h2", text: "2. New hires take too long to become useful" },
      { kind: "p", text: "If ramp time is measured in quarters instead of weeks, the operating knowledge is not documented, it is being transmitted person-to-person, which does not scale." },
      { kind: "h2", text: "3. Meetings have replaced systems" },
      { kind: "p", text: "Recurring status meetings often exist because no system of record is trusted. The meeting is the source of truth, which means the truth is only as good as the attendance." },
      { kind: "h2", text: "4. Cross-functional work stalls at the seams" },
      { kind: "p", text: "When work crosses two or more teams and consistently loses momentum, the model has not defined the handoffs. The people are not the problem; the interface is missing." },
      { kind: "h2", text: "5. Tools have accumulated faster than they have been integrated" },
      { kind: "p", text: "Every symptom got its own subscription. Nothing talks to anything. Reporting requires exports, joins, and hope." },
      { kind: "h2", text: "6. Leadership is spending more time on operations than on strategy" },
      { kind: "p", text: "When executives are consistently pulled into the work rather than steering it, the layer beneath them is not sufficient for the size of the organization." },
      { kind: "h2", text: "7. The plan for next year looks a lot like this year, because nothing else could fit" },
      { kind: "p", text: "This is the quietest sign, and the most important. If the strategy is being sized to the model, the model is now the constraint." },
      { kind: "h2", text: "What to do next" },
      { kind: "p", text: "Restructuring an operating model is not a re-org. It is a coordinated redesign of people, process, and technology, sequenced so the organization keeps running while it is being rebuilt. That is where an outside practitioner earns their place: not to have opinions, but to sequence." },
    ],
  },
  {
    slug: "gov-contractor-readiness",
    type: "Checklist",
    title: "Government Contractor Readiness Checklist",
    desc: "A pre-award readiness checklist covering SAM, NAICS, compliance posture, and capability positioning.",
    tag: "Government",
    image: "https://images.unsplash.com/photo-1541872703-74c5e44368f9?w=1600&q=80",
    author: "Kentavious Patman",
    author_role: "Founder & Managing Principal",
    read_time: "5 min read",
    date: "2026-08-22",
    cta: { label: "Talk to Government & Defense", to: "/government", eyebrow: "Ready to compete for federal work?" },
    body: [
      { kind: "p", text: "Winning government work does not begin with a proposal. It begins with a readiness posture, the set of registrations, classifications, documentation, and capability positioning that make an organization credible before an opportunity is ever released. This checklist covers the pre-award posture we consider table stakes." },
      { kind: "h2", text: "Registrations & identifiers" },
      { kind: "list", items: [
        "SAM.gov registration is active, current, and reviewed at least quarterly.",
        "UEI is issued and referenced consistently across capability statements and marketing.",
        "Primary and secondary NAICS codes are chosen deliberately, not inherited from a template.",
        "SBA size standards for each NAICS have been reviewed against current revenue and employee counts.",
      ] },
      { kind: "h2", text: "Set-aside and certification eligibility" },
      { kind: "list", items: [
        "8(a), HUBZone, SDVOSB, WOSB, and VOSB eligibility has been assessed against current ownership and operations.",
        "Certifications actively pursued are tracked with a documented owner and target date.",
        "Certification supporting documents (financials, ownership, addresses, veteran status) are current and centrally stored.",
      ] },
      { kind: "h2", text: "Compliance posture" },
      { kind: "list", items: [
        "FAR/DFARS familiarity for the relevant contract vehicles is documented, not assumed.",
        "Cybersecurity posture (NIST SP 800-171, CMMC where applicable) has been baselined honestly.",
        "Financial and accounting systems can support cost accounting if required (DCAA-adjacent readiness).",
        "Insurance limits and coverage match the ceilings on the vehicles being pursued.",
      ] },
      { kind: "h2", text: "Capability positioning" },
      { kind: "list", items: [
        "Capability statement is one page, current, tailored to the NAICS being targeted, and free of generic marketing language.",
        "Past performance is documented with contract number, agency, POP, and a concise outcome statement, even for commercial work.",
        "Teaming posture is decided: prime, sub, joint venture, mentor-protégé, and the target partners have been identified.",
        "A written capture cadence exists: what agencies, what vehicles, what timelines, and who owns each opportunity.",
      ] },
      { kind: "h2", text: "How to use this checklist" },
      { kind: "p", text: "This is a diagnostic, not a scorecard. If more than a third of these items are ambiguous, the organization is not yet ready to price aggressively into federal work, and the shortest path to competitiveness is closing those gaps, not writing another proposal." },
    ],
  },
  {
    slug: "temp-fractional-fulltime",
    type: "Guide",
    title: "Temporary vs. Fractional vs. Full-Time Workforce Support",
    desc: "How to decide which workforce model fits the scope, duration, and cost profile you actually need.",
    tag: "Workforce",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&q=80",
    author: "Jeremy Cortez",
    author_role: "Principal, Logistics & Infrastructure",
    read_time: "6 min read",
    date: "2026-08-22",
    cta: { label: "Explore Workforce Solutions", to: "/workforce-solutions", eyebrow: "Match the model to the mission" },
    body: [
      { kind: "p", text: "Most workforce hiring decisions get made under pressure. A role opens, a project accelerates, a client escalates, and the default answer is to open a full-time req. Sometimes that is right. Often it is not. Choosing the wrong model is expensive in a different way than choosing the wrong person: it is expensive slowly, and it is expensive structurally." },
      { kind: "p", text: "Here is how we help clients decide between temporary, fractional, and full-time support." },
      { kind: "h2", text: "Temporary support" },
      { kind: "p", text: "Temporary is the right answer when the need is bounded in time, defined in scope, and does not require deep institutional context. Coverage for leave, ramp support for a launch, event staffing, backfill during a search, these are temp problems. The mistake is stretching a temp arrangement into an ongoing dependency without converting it deliberately." },
      { kind: "h2", text: "Fractional support" },
      { kind: "p", text: "Fractional is the right answer when the need is ongoing but not full-time, typically a function that requires senior judgment (HR, finance, communications, technology leadership) at a level the organization cannot yet justify hiring for. Fractional buys you leadership, discipline, and an operating layer without the cost profile of a full-time executive." },
      { kind: "h2", text: "Full-time hiring" },
      { kind: "p", text: "Full-time is the right answer when the workload is durable, the institutional knowledge is high-value, and the role has clear growth beyond its current scope. If any of those three conditions is missing, a full-time hire is more expensive than the org chart suggests." },
      { kind: "h2", text: "A simple decision frame" },
      { kind: "list", items: [
        "Is the need bounded in time? If yes, temporary or project-based.",
        "Is the need ongoing but under 20 hours a week of senior judgment? If yes, fractional.",
        "Is the need durable, expanding, and institutionally sensitive? If yes, full-time.",
        "Is the need ambiguous? Start with fractional or embedded support and let the scope clarify itself before you convert.",
      ] },
      { kind: "h2", text: "The embedded option" },
      { kind: "p", text: "There is a fourth option worth naming: embedded personnel who remain on our payroll while operating inside your team. This is the right answer when you need continuity and integration but not the overhead of direct employment, particularly for regulated environments, temporary scaling, or roles you intend to convert once the shape of the work is proven." },
    ],
  },
  {
    slug: "prepare-for-workforce-audit",
    type: "Guide",
    title: "How to Prepare Your Organization for a Workforce Audit",
    desc: "A step-by-step approach to documentation, controls, and readiness ahead of an internal or external audit.",
    tag: "Compliance",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&q=80",
    author: "Kentavious Patman",
    author_role: "Founder & Managing Principal",
    read_time: "7 min read",
    date: "2026-08-22",
    cta: { label: "Take the Business Assessment", to: "/business-assessment", eyebrow: "Baseline your audit readiness" },
    body: [
      { kind: "p", text: "A workforce audit, whether internal, client-driven, regulatory, or acquisition-related, is a stress test of the discipline you have been running all along. Preparation is less about producing new documents and more about proving that the documents you already have reflect what the organization actually does. The gap between those two is where audits fail." },
      { kind: "h2", text: "Phase 1 · Establish the scope" },
      { kind: "p", text: "Every audit has a scope. Get it in writing early: which entities, which time periods, which populations, which controls. Ambiguity in scope creates ambiguity in findings, and findings are what get reported." },
      { kind: "h2", text: "Phase 2 · Assemble the file room" },
      { kind: "list", items: [
        "Personnel files complete for the audit population, hire documentation, offer letters, I-9s, tax forms, acknowledgments.",
        "Payroll records aligned to the audit period, with exception logs for corrections.",
        "Time and attendance records for non-exempt employees, including overtime calculations.",
        "Benefits enrollment and eligibility documentation, including any COBRA activity.",
        "Training records for role-specific and compliance-mandated training.",
        "Discipline and separation documentation for the audit period, with legal review dates where applicable.",
      ] },
      { kind: "h2", text: "Phase 3 · Test the controls" },
      { kind: "p", text: "Before the auditor tests them, you should. Pull a random sample from your own population and walk through each control end-to-end. Where you find gaps, document the correction and the process improvement, this is what turns a finding into a managed exception." },
      { kind: "h2", text: "Phase 4 · Prepare the narrative" },
      { kind: "p", text: "Audits are not only about documents. They are about the story the documents tell. A prepared organization can articulate, in plain language, what the workforce program is designed to do, how it is operated, who owns it, and how exceptions are handled. That narrative should be rehearsed by the people who will be interviewed." },
      { kind: "h2", text: "Phase 5 · Manage the response" },
      { kind: "p", text: "During fieldwork, centralize communication with the auditor through a single point of contact. Track every request, every document produced, and every question asked. Never speculate on the record, commit to responses in writing, after review." },
      { kind: "h2", text: "After the audit" },
      { kind: "p", text: "The most valuable output of any audit is the corrective action plan. Treat findings as a prioritized backlog, not a compliance chore. The organizations that get stronger from audits are the ones that use them as forcing functions on the operating layer they had been meaning to fix anyway." },
    ],
  },
];

// Legacy roles removed, the site now uses the Talent Network model (see TALENT_CATEGORIES below).
export const CAREERS = [];

// Talent Network categories. This is not an active job list, it is the set
// of specialties Consilium Pro invites experienced professionals to submit
// interest against for future client and firm engagements.
export const TALENT_CATEGORIES = [
  { label: "Human Resources",              icon: "Users" },
  { label: "Administrative Support",       icon: "ClipboardList" },
  { label: "Marketing & Communications",   icon: "Megaphone" },
  { label: "Technology & IT",              icon: "Cpu" },
  { label: "Cybersecurity",                icon: "ShieldCheck" },
  { label: "Training & Facilitation",      icon: "GraduationCap" },
  { label: "Project Management",           icon: "KanbanSquare" },
  { label: "Accounting & Finance",         icon: "Coins" },
  { label: "Logistics",                    icon: "Truck" },
  { label: "Security",                     icon: "Lock" },
  { label: "Government Support",           icon: "Landmark" },
  { label: "Reception & Concierge",        icon: "Bell" },
  { label: "Business Operations",          icon: "Settings2" },
];

// Homepage credibility categories, replaces the old (unsupported) testimonial section.
export const CREDIBILITY_AREAS = [
  { icon: "Users",         title: "Workforce & People Operations" },
  { icon: "Landmark",      title: "Government & High-Compliance Environments" },
  { icon: "ShieldCheck",   title: "Security & Intelligence" },
  { icon: "Cpu",           title: "Technology & Digital Solutions" },
  { icon: "GraduationCap", title: "Training & Leadership Development" },
  { icon: "Truck",         title: "Operations, Logistics & Growth" },
];

// Homepage "How It Works", the five-step delivery arc.
export const HOW_IT_WORKS = [
  { step: "Assess",   desc: "Start with an assessment or discovery conversation." },
  { step: "Diagnose", desc: "We identify workforce, operational, compliance, technology, growth, and organizational gaps." },
  { step: "Design",   desc: "We build the appropriate consulting, workforce, fractional, training, or project-based solution." },
  { step: "Deliver",  desc: "Our Principals and specialists execute the agreed solution." },
  { step: "Measure",  desc: "We review outcomes, identify improvements, and recommend next steps." },
];

// Homepage trust bar, currently supportable claims only.
export const TRUST_BAR = ["Atlanta-Based", "Veteran-Led", "Principal-Led", "Multidisciplinary", "Nationwide Support"];

// Testimonials intentionally removed, the site replaces the testimonial
// section with "Built on Practitioner Experience" credibility areas.
export const TESTIMONIALS = [];

export const LEADERSHIP_INTRO = "Consilium Pro is a Principal-led consulting and fractional services firm built by experienced practitioners across business, government, military, security, technology, training, communications, and operations.";

export const LEADERSHIP = [
  {
    name: "Kentavious D. Patman",
    initials: "KP",
    image: "https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/eetpo2mk_Patman%2C%20K%20Headshot.png",
    role: "Founder & Managing Principal",
    practice: "Firmwide Strategy & Organizational Direction",
    bio: "Kentavious founded Consilium Pro Workforce Solutions and leads firmwide strategy and organizational direction. He brings senior People Operations and workforce leadership experience, with a career spanning security, government, aerospace, and other high-compliance environments where documentation, discipline, and outcomes are non-negotiable. He advises organizations on building scalable workforce and operating systems, aligning human resources, organizational operations, compliance, leadership, and business strategy inside a single, coherent framework. His military leadership experience shapes an executive-level approach to organizational problem-solving. Kentavious's focus is aligning people, operations, compliance, and strategy so growth is sustainable and mission-ready.",
    expertise: ["Workforce Strategy", "People Operations", "Compliance", "Leadership Development", "Training & Facilitation", "Organizational Leadership", "Business Strategy"],
    areas: ["Workforce Strategy", "Human Resources & People Operations", "Organizational Operations", "Compliance", "Leadership Development", "Training & Curriculum", "Facilitation", "Professional Development", "Business Strategy", "Government & High-Compliance Environments"],
  },
  {
    name: "Jeremy Cortez",
    initials: "JC",
    role: "Principal, Logistics & Infrastructure",
    practice: "Logistics, Infrastructure & Operational Readiness",
    bio: "Jeremy leads logistics, infrastructure, operational readiness, and related support at Consilium Pro. His background combines military intelligence and geospatial analysis with deep technical and mechanical experience, giving him a practitioner's view of how strategy actually gets executed. His expertise covers logistics, infrastructure, operational readiness, fleet support, asset coordination, and process execution. Jeremy focuses on turning strategic requirements into executable operational systems, the kind that survive contact with real conditions. He performs well in structured, mission-focused environments and emphasizes reliability, readiness, and logistics discipline. His focus is helping organizations maintain operational continuity and build scalable infrastructure.",
    expertise: ["Logistics", "Infrastructure", "Operational Readiness", "Fleet", "Mission Support"],
    areas: ["Logistics", "Infrastructure", "Operational Readiness", "Fleet Support", "Asset Coordination", "Process Execution"],
  },
  {
    name: "Hadiya Rucker",
    initials: "HR",
    image: "https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/tj00qfcm_Rucker%2C%20Hadiya.png",
    role: "Principal, Public Affairs & Brand Strategy",
    practice: "Public Affairs, Communications & External Engagement",
    bio: "Hadiya leads public affairs, communications, brand positioning, and external engagement at Consilium Pro. She brings corporate communications and customer-facing experience along with event and stakeholder-engagement work that put her in front of internal teams, community partners, and executive audiences. Her expertise spans public affairs, brand strategy, marketing communications, social media, content strategy, and stakeholder engagement. Hadiya helps organizations communicate clearly and consistently with internal and external audiences, translating organizational identity into public-facing messaging that holds up under scrutiny. She emphasizes reputation, visibility, communication consistency, and audience engagement, and focuses on strengthening how organizations are understood and experienced.",
    expertise: ["Public Affairs", "Brand Strategy", "Communications", "Content", "Stakeholder Engagement"],
    areas: ["Public Affairs", "Brand Strategy", "Marketing Communications", "Social Media", "Content Strategy", "Stakeholder Engagement"],
  },
  {
    name: "Brashad Rouland",
    initials: "BR",
    role: "Principal, Growth & Community Partnerships",
    practice: "Growth Strategy, Partnerships & Community Engagement",
    bio: "Brashad leads growth strategy, relationship development, partnerships, and community engagement at Consilium Pro. He brings 15+ years of Georgia Army National Guard experience, including a geospatial intelligence and intelligence-analysis background and leadership as a GEOINT NCOIC, alongside civilian security and supervisory experience. His expertise covers business development, partnerships, community engagement, relationship management, intelligence-informed planning, and security program advisory. Brashad identifies opportunities and builds the relationships that turn them into sustainable engagements, drawing on military leadership, community connectivity, and practical field experience. His focus is creating durable partnerships and new growth opportunities for the firm and its clients.",
    expertise: ["Growth", "Partnerships", "Community Engagement", "Client Success", "Intelligence", "Security Advisory"],
    areas: ["Business Development", "Partnerships", "Community Engagement", "Relationship Management", "Client Experience & Success", "Intelligence-Informed Planning", "Security Program Advisory"],
  },
  {
    name: "Darien Ford",
    initials: "DF",
    role: "Principal, Technology & Digital Solutions",
    practice: "Technology Strategy, Digital Solutions & Assessment",
    bio: "Darien leads technology strategy, digital solutions, and systems modernization at Consilium Pro. He brings 20+ years of technical and military communications experience, with an information technology and cybersecurity background and military communications specialties that inform how he approaches secure, mission-ready environments. His expertise spans information technology, cybersecurity, digital transformation, communications systems, technology infrastructure, and technology readiness. Darien helps organizations evaluate and strengthen technology environments through secure, practical, scalable solutions. His focus is aligning technology investments with business and mission requirements so organizations get durable value rather than shelfware.",
    expertise: ["Technology", "Cybersecurity", "Digital Solutions", "Communications Systems", "IT Strategy"],
    areas: ["Information Technology", "Cybersecurity", "Digital Transformation", "Communications Systems", "Technology Infrastructure", "Technology Readiness"],
  },
];

export const GOV_DIVISION = {
  intro: "Consilium Pro maintains a dedicated Government & Defense Solutions Division to support federal agencies, defense contractors, intelligence organizations, and government partners. Led by cleared professionals, the division combines expertise in executive strategy, technology, intelligence, logistics, and workforce consulting to deliver mission-focused solutions.",
  leads: [
    { name: "Kentavious Patman", focus: "Executive Strategy & Government Contracting" },
    { name: "Darien Ford", focus: "Technology, Cybersecurity & Communications" },
    { name: "Brashad Rouland", focus: "Government Business Development & Intelligence" },
    { name: "Jeremy Cortez", focus: "Infrastructure, Fleet & Logistics" },
  ],
};
